'use client'

import { WIDE_ASPECT_RATIO } from '@/components/scenario/aspectRatio'
import { X } from 'lucide-react'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import type { Hotspot, InteractiveButton, PlayerConfig } from '../../../types/interactiveVideo'
import InteractiveVideoPlayer from '../InteractiveVideoPlayer'
import { useFrameFullscreenExit } from '../shadow-host-portal/useFrameFullscreenExit'
import { WIDE_AGENDA_SIDE_MEDIA_QUERY, wideVideoWidthCss } from './wideFrameSize'
import { WIDE_FONT_FAMILY } from './wideFont'

/** 縦積み表示に切り替える画面（スマホの縦持ち） */
const STACKED_MEDIA_QUERY = '(max-width: 639px) and (orientation: portrait)'

export type WideModalProps = {
  scenarioId: string
  config: PlayerConfig
  onClose: () => void
  onVideoEnd: (nodeId: string) => void
  onVideoChange: (nodeId: string) => void
  onButtonClick: (button: InteractiveButton) => void
  /** 動画内のクリック領域が押された（計測用・ADR-0022） */
  onHotspotClick?: (hotspot: Hotspot) => void | Promise<void>
}

/**
 * ワイド型の再生モーダル。ページ内のポスターをクリックしたら、BOX と同じく画面を覆う
 * オーバーレイの中で大きく再生する（16:9 のまま可能な限り大きく）。
 *
 * インライン再生だと埋め込み先の本文幅に縛られて映像が小さくなり、分岐オーバーレイや
 * 終了画面の文字も潰れる。ポスターはページのフローに残したままモーダルを上に重ねるので、
 * 閉じたときに埋め込み先のレイアウトは動かない（ADR-0016 の「枠を動かさない」を維持）。
 */
const WideModal: FC<WideModalProps> = ({
  scenarioId,
  config,
  onClose,
  onVideoEnd,
  onVideoChange,
  onButtonClick,
  onHotspotClick,
}) => {
  const frameRef = useRef<HTMLDivElement>(null)

  // クリック起点で開くので音あり再生できる（共通 config は自動再生前提で muted: true）
  const unmutedConfig = useMemo(() => ({ ...config, muted: false }), [config])

  // 全画面のまま次の動画へ進んでも枠の比率を保つ（全画面時は画面比率に従う）
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isCloseHovered, setIsCloseHovered] = useState(false)
  useEffect(() => {
    const sync = () => setIsFullscreen(document.fullscreenElement === frameRef.current)
    document.addEventListener('fullscreenchange', sync)
    return () => document.removeEventListener('fullscreenchange', sync)
  }, [])

  // iframe 埋め込みページ（/embed）では、モーダルの間 document 全体が全画面になっている。
  // プレイヤー枠だけの全画面（frameRef）を抜けても document の全画面は残るので、
  // document まで全画面が解除されたときだけ閉じる（frameFullscreen 側で判定）。
  useFrameFullscreenExit(true, onClose)

  // スマホの縦画面では 16:9 の映像が小さくなるので、選択肢を映像に重ねず動画の下に並べる（縦積み）。
  // 全画面にしたときは横向きで見るのが前提なので、従来どおり映像に重ねる。
  const [isPortraitPhone, setIsPortraitPhone] = useState(false)
  useEffect(() => {
    const media = window.matchMedia(STACKED_MEDIA_QUERY)
    const sync = () => setIsPortraitPhone(media.matches)
    sync()
    media.addEventListener('change', sync)
    return () => media.removeEventListener('change', sync)
  }, [])
  const isStacked = isPortraitPhone && !isFullscreen

  // 目次（ADR-0025）を動画の左に重ねて出せる広い画面か。狭い画面はプレイヤーが左上のボタンから開く
  const matchesAgendaSide = () => typeof window !== 'undefined' && window.matchMedia(WIDE_AGENDA_SIDE_MEDIA_QUERY).matches
  const [isAgendaSideScreen, setIsAgendaSideScreen] = useState(matchesAgendaSide)
  useEffect(() => {
    const media = window.matchMedia(WIDE_AGENDA_SIDE_MEDIA_QUERY)
    const sync = () => setIsAgendaSideScreen(media.matches)
    media.addEventListener('change', sync)
    return () => media.removeEventListener('change', sync)
  }, [])
  const hasAgenda = (config.agenda?.length ?? 0) > 0
  const agendaPlacement = hasAgenda && !isStacked && isAgendaSideScreen ? 'side' : 'overlay'
  // 目次は動画に重ねるので、枠は常に 16:9 の動画だけの大きさにする
  const videoWidth = wideVideoWidthCss(48)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // プレイヤー枠を全画面にしている間の Esc はブラウザが全画面解除に使うので、モーダルまで閉じない。
      // iframe 埋め込みページでは document 全体が全画面になっているが、それはモーダルと同じ寿命なので
      // Esc で閉じてよい（閉じれば全画面も解除される）。
      if (event.key === 'Escape' && document.fullscreenElement !== frameRef.current) onClose()
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  return (
    <div
      data-testid="wide-modal"
      role="dialog"
      aria-modal="true"
      aria-label="インタラクティブ動画"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isStacked ? 0 : '24px',
        background: 'rgba(15, 23, 42, 0.72)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        pointerEvents: 'auto',
        fontFamily: WIDE_FONT_FAMILY,
      }}
    >
      <div style={{ position: 'absolute', inset: 0 }} onClick={onClose} aria-hidden="true" />

      <div
        ref={frameRef}
        data-testid="wide-modal-frame"
        style={{
          position: 'relative',
          // 16:9 を保ったまま、幅と高さの両方に収まる最大サイズを選ぶ。
          // 縦積みは横幅いっぱいに広げ、高さは動画＋下の選択肢欄に任せる
          width: isFullscreen || isStacked ? '100%' : videoWidth,
          aspectRatio: isFullscreen || isStacked ? undefined : WIDE_ASPECT_RATIO,
          height: isFullscreen ? '100%' : undefined,
          maxHeight: isStacked ? '100%' : undefined,
          overflowX: 'hidden',
          overflowY: isStacked ? 'auto' : 'hidden',
          borderRadius: isFullscreen || isStacked ? 0 : '16px',
          background: '#000',
          boxShadow: 'none',
          animation: 'moovy-wide-open 240ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        <style>{`@keyframes moovy-wide-open { from { opacity: 0; transform: translateY(10px) scale(.99) } to { opacity: 1; transform: translateY(0) scale(1) } }`}</style>

        <InteractiveVideoPlayer
          config={unmutedConfig}
          scenarioId={scenarioId}
          containerMode={true}
          variant="wide"
          wideLayout={isStacked ? 'stacked' : 'overlay'}
          wideAgendaPlacement={agendaPlacement}
          fullscreenTargetRef={frameRef}
          onClose={onClose}
          onVideoEnd={onVideoEnd}
          onVideoChange={onVideoChange}
          onButtonClick={onButtonClick}
          onHotspotClick={onHotspotClick}
        />
      </div>

      {/* 閉じるボタンは枠の外（右上）に置く。ワイド型は下部コントロールバーが枠内を使うため。 */}
      {!isFullscreen && (
        <button
          type="button"
          onClick={onClose}
          onMouseEnter={() => setIsCloseHovered(true)}
          onMouseLeave={() => setIsCloseHovered(false)}
          aria-label="閉じる"
          style={{
            position: 'absolute',
            top: '18px',
            right: '18px',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '38px',
            height: '38px',
            padding: 0,
            // ほかの×ボタンと同じく、乗せると背景に色が付いて×がはっきりする（200ms でなめらかに変える）
            border: `1px solid ${isCloseHovered ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.28)'}`,
            borderRadius: '50%',
            background: isCloseHovered ? 'rgba(255,255,255,0.22)' : 'rgba(15,23,42,0.55)',
            color: '#fff',
            backdropFilter: 'blur(8px)',
            cursor: 'pointer',
            transition: 'background 200ms ease, border-color 200ms ease',
          }}
        >
          <X size={19} />
        </button>
      )}
    </div>
  )
}

export default WideModal
